/**
 * Scheduled Task（定时任务）types.
 *
 * Field shapes mirror backend ScheduledTaskResponse / ScheduledTaskCreate /
 * ScheduledTaskUpdate schemas (app/modules/jerry/schemas.py). The API layer
 * camel-cases JSON, so interfaces here use camelCase. String values for
 * targetType / status / frequency / trigger kind / run status stay aligned
 * with backend enums (app/modules/jerry/enums.py: ScheduledTaskTargetType /
 * ScheduledTaskStatus / ScheduledTaskFrequency / ScheduledTaskTriggerKind /
 * ScheduledTaskRunStatus). Contract: docs/iterations/scheduled-tasks-v1-plan.md §6/§8.
 */

import type { AgentRunStatus } from "./agentRun";

/** What a scheduled task does when it fires (ScheduledTaskTargetType).
 *
 * `pipeline_template`（波次 2）：按模板周期执行——每期由输入解析器动态圈定
 * 文档集，实例化模板后执行，与 `pipeline_instance` 的「重放同一实例」相对。
 * `agent_run`（波次 3 长程线）：每期物化一条长程 agent run（agentic 模板
 * 目标）。词表漏登导致定时任务列表类型列空白（2026-08-28 WP3 验收发现）。 */
export type ScheduledTaskTargetType =
  | "pipeline_instance"
  | "matter_reminder"
  | "pipeline_template"
  | "agent_run";

/**
 * 模板级目标的输入解析器（波次 2 契约）。v1 仅一种：按事项圈定每期新上传文档。
 * 值与后端枚举对齐；扩展新解析器时先登记词表再动代码。
 */
export type ScheduledTaskInputResolver = "matter_documents_delta";

/** Chinese display labels for input resolvers (pipeline_template targets only). */
export const SCHEDULED_TASK_INPUT_RESOLVER_LABELS: Record<ScheduledTaskInputResolver, string> = {
  matter_documents_delta: "事项新上传文档",
};

/** 解析器配置（POST/PATCH /scheduled-tasks 的 resolverConfig）。字段可选以匹配
 * 后端 exclude_unset 语义：更新时缺省即"不变"。 */
export interface ScheduledTaskResolverConfig {
  /** 每期纳入文档上限；null = 后端默认。 */
  maxDocuments?: number | null;
  /** 首期回看窗口天数（首次运行没有「上次运行」可参照）；null = 后端默认。 */
  firstRunWindowDays?: number | null;
}

/**
 * Lifecycle (ScheduledTaskStatus). `next_run_at` is null unless `active`.
 * `finished`（AI 任务中心）：单次任务触发后的终态——不再重臂，next_run_at
 * 恒为 null，执行状态以关联 agent run 为准展示。
 */
export type ScheduledTaskStatus = "active" | "paused" | "disabled" | "finished";

/** Outcome of one firing (ScheduledTaskRunStatus). */
export type ScheduledTaskRunStatus =
  | "triggered"
  | "running"
  | "completed"
  | "failed"
  | "skipped";

/**
 * Recurrence preset (ScheduledTaskFrequency). Cron is never exposed in the UI.
 * `once` 仅对 agent_run 目标开放（AI 任务的单次执行：创建即触发一次，
 * POST /scheduled-tasks 拒绝 once——单次任务的唯一创建入口是 POST /agent-runs）。
 */
export type ScheduledTaskFrequency =
  | "once"
  | "hourly"
  | "daily"
  | "weekly"
  | "monthly"
  | "custom_interval";

/** How a run was started (ScheduledTaskTriggerKind). `run-now` writes `manual`. */
export type ScheduledTaskTriggerKind = "scheduled" | "manual";

/** Chinese display labels for target types. `agent_run` 界面标签为「AI 任务」
 * （AI 任务中心合并后，「长程任务」一词从界面文案退役；内部标识 agent_run
 * 保留）。 */
export const SCHEDULED_TASK_TARGET_TYPE_LABELS: Record<ScheduledTaskTargetType, string> = {
  pipeline_instance: "工作流",
  matter_reminder: "事项提醒",
  pipeline_template: "按模板周期",
  agent_run: "AI 任务",
};

/** Selectable target-type options for filters / forms. */
export const SCHEDULED_TASK_TARGET_TYPE_OPTIONS: { value: ScheduledTaskTargetType; label: string }[] = (
  Object.entries(SCHEDULED_TASK_TARGET_TYPE_LABELS) as [ScheduledTaskTargetType, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for statuses. */
export const SCHEDULED_TASK_STATUS_LABELS: Record<ScheduledTaskStatus, string> = {
  active: "运行中",
  paused: "已暂停",
  disabled: "已停用",
  finished: "已完成",
};

/** Selectable status options for filters. */
export const SCHEDULED_TASK_STATUS_OPTIONS: { value: ScheduledTaskStatus; label: string }[] = (
  Object.entries(SCHEDULED_TASK_STATUS_LABELS) as [ScheduledTaskStatus, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for run outcomes. */
export const SCHEDULED_TASK_RUN_STATUS_LABELS: Record<ScheduledTaskRunStatus, string> = {
  triggered: "已触发",
  running: "执行中",
  completed: "成功",
  failed: "失败",
  skipped: "已跳过",
};

/** Selectable run-status options for filters. */
export const SCHEDULED_TASK_RUN_STATUS_OPTIONS: { value: ScheduledTaskRunStatus; label: string }[] = (
  Object.entries(SCHEDULED_TASK_RUN_STATUS_LABELS) as [ScheduledTaskRunStatus, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for frequency presets. */
export const SCHEDULED_TASK_FREQUENCY_LABELS: Record<ScheduledTaskFrequency, string> = {
  once: "单次",
  hourly: "每小时",
  daily: "每天",
  weekly: "每周",
  monthly: "每月",
  custom_interval: "自定义间隔",
};

/** Selectable frequency options for the frequency picker. */
export const SCHEDULED_TASK_FREQUENCY_OPTIONS: { value: ScheduledTaskFrequency; label: string }[] = (
  Object.entries(SCHEDULED_TASK_FREQUENCY_LABELS) as [ScheduledTaskFrequency, string][]
).map(([value, label]) => ({ value, label }));

/** Chinese display labels for trigger kind (used on run-history rows). */
export const SCHEDULED_TASK_TRIGGER_KIND_LABELS: Record<ScheduledTaskTriggerKind, string> = {
  scheduled: "定时触发",
  manual: "手动执行",
};

/** Selectable trigger-kind options. */
export const SCHEDULED_TASK_TRIGGER_KIND_OPTIONS: { value: ScheduledTaskTriggerKind; label: string }[] = (
  Object.entries(SCHEDULED_TASK_TRIGGER_KIND_LABELS) as [ScheduledTaskTriggerKind, string][]
).map(([value, label]) => ({ value, label }));

/** Weekday value used by `byWeekday` — 0=周一 … 6=周日 (dateutil.rrule.MO..SU mapping). */
export type ScheduledTaskWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Chinese display labels for weekdays, 0=周一 … 6=周日. */
export const WEEKDAY_LABELS: Record<ScheduledTaskWeekday, string> = {
  0: "周一",
  1: "周二",
  2: "周三",
  3: "周四",
  4: "周五",
  5: "周六",
  6: "周日",
};

/** Selectable weekday options for the weekly frequency picker. */
export const WEEKDAY_OPTIONS: { value: ScheduledTaskWeekday; label: string }[] = (
  Object.entries(WEEKDAY_LABELS) as [string, string][]
).map(([value, label]) => ({ value: Number(value) as ScheduledTaskWeekday, label }));

/** Last-run summary inlined on `ScheduledTask.lastRun` (subset of ScheduledTaskRun). */
export interface ScheduledTaskLastRunSummary {
  id: string;
  status: ScheduledTaskRunStatus;
  scheduledAt: string;
  triggeredAt: string | null;
  executionId: string | null;
  errorMessage: string | null;
  triggerKind: ScheduledTaskTriggerKind;
  /** agent_run 目标：本期派发出的 agent run 链接与实时执行态（六态以
   * agent run 为准——audit 行对 agent_run 目标只代表「派发完成」）。 */
  agentRunId?: string | null;
  agentRunStatus?: AgentRunStatus | null;
  /** 服务端截断 200 字符的执行摘要。 */
  agentRunSummary?: string | null;
}

/** A scheduled task record (ScheduledTaskResponse — list item and detail share this shape). */
export interface ScheduledTask {
  id: string;
  name: string;
  description: string;
  targetType: ScheduledTaskTargetType;
  status: ScheduledTaskStatus;
  instanceId: string | null;
  /** Inlined instance name, avoids a second request. */
  instanceName: string | null;
  /** pipeline_template 目标：绑定的模板 id；其余目标类型为 null。 */
  templateId: string | null;
  /** pipeline_instance 行沿用实例所属模板名；pipeline_template 行即目标模板名。 */
  templateName: string | null;
  matterId: string | null;
  matterName: string | null;
  /** pipeline_template 目标的输入解析器；其余目标类型为 null。 */
  inputResolver: ScheduledTaskInputResolver | null;
  /** 解析器配置；非 pipeline_template 目标为 null。 */
  resolverConfig: ScheduledTaskResolverConfig | null;
  /** agent_run 目标的任务目标：once 存用户直传目标，周期任务存可选自定义
   * 目标（空则每期按模板 goal_template 渲染）；其余目标类型为 null。 */
  goal: string | null;
  /** agent_run 目标的预算三元组（其余目标类型恒 null；null = 不限/走默认）。 */
  maxSteps: number | null;
  maxWallClockMinutes: number | null;
  maxTotalTokens: number | null;
  reminderTitle: string | null;
  reminderNote: string | null;
  notifyUserIds: string[];
  frequency: ScheduledTaskFrequency;
  atHour: number | null;
  atMinute: number | null;
  byWeekday: ScheduledTaskWeekday | null;
  byMonthday: number | null;
  intervalMinutes: number | null;
  timezone: string;
  /** Server-side `describe()` output, e.g. 「每周一 09:00」. */
  frequencyText: string;
  startsAt: string | null;
  endsAt: string | null;
  maxRuns: number | null;
  runCount: number;
  lastTriggeredAt: string | null;
  /** Null when `status !== "active"`. */
  nextRunAt: string | null;
  disabledReason: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Most recent run, null if the task has never fired. */
  lastRun: ScheduledTaskLastRunSummary | null;
}

/** 模板级目标一期实际圈定的输入集（波次 2 契约 resolvedInputs）。 */
export interface ScheduledTaskRunResolvedInputs {
  /** 本期实际纳入执行的文档 id（截断后）。 */
  documentIds: string[];
  /** 本期命中的文档总数（截断前）。 */
  totalHit: number;
  /** totalHit 超过上限被截断时为 true——界面须以警示色明示有文档未纳入。 */
  truncated: boolean;
  /** 圈定窗口（首期回看 / 上期以来）。 */
  periodStart: string | null;
  periodEnd: string | null;
}

/** One execution-history row (ScheduledTaskRunResponse). */
export interface ScheduledTaskRun {
  id: string;
  taskId: string;
  scheduledAt: string;
  triggeredAt: string | null;
  status: ScheduledTaskRunStatus;
  triggerKind: ScheduledTaskTriggerKind;
  executionId: string | null;
  /** pipeline_template 运行：本期实例化出的工作流实例 id。 */
  instanceId: string | null;
  errorMessage: string | null;
  skippedReason: string | null;
  /** pipeline_template 运行：本期输入解析结果；其余为 null。 */
  resolvedInputs: ScheduledTaskRunResolvedInputs | null;
  /** agent_run 目标：本期派发出的 agent run 链接与实时执行态。 */
  agentRunId?: string | null;
  agentRunStatus?: AgentRunStatus | null;
  /** 服务端截断 200 字符的执行摘要。 */
  agentRunSummary?: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// 期产物人审闭环（波次 2 契约，targetType=pipeline_template 专属）
// ---------------------------------------------------------------------------

/** 整期人审状态。pending_review 之外均为只读留痕展示。 */
export type ScheduledRunReviewStatus = "pending_review" | "accepted" | "returned";

/** Chinese display labels for run review statuses. */
export const SCHEDULED_RUN_REVIEW_STATUS_LABELS: Record<ScheduledRunReviewStatus, string> = {
  pending_review: "待人审",
  accepted: "已采纳",
  returned: "已退回",
};

/** Selectable run-review status options. */
export const SCHEDULED_RUN_REVIEW_STATUS_OPTIONS: {
  value: ScheduledRunReviewStatus;
  label: string;
}[] = (
  Object.entries(SCHEDULED_RUN_REVIEW_STATUS_LABELS) as [ScheduledRunReviewStatus, string][]
).map(([value, label]) => ({ value, label }));

/**
 * 一个步骤的期产物。契约冻结了 stepOutputs 数组名，条目字段按防御式可选建模
 * ——后端落地后以实际返回对齐，渲染层对缺失字段一律不渲染、不补占位文案。
 */
export interface ScheduledRunStepOutput {
  /** 步骤标识，表态的 targetRef。 */
  key: string;
  stepName: string | null;
  title: string | null;
  /** 产物类型（如 text / document / report），仅展示用。 */
  outputKind: string | null;
  summary: string | null;
  content: string | null;
  documentId: string | null;
  createdAt: string | null;
}

/** 已另存文档卡（savedDocuments 条目，Document 行的结构子集）。 */
export interface ScheduledRunSavedDocument {
  id: string;
  title: string;
  matterId: string | null;
  createdAt: string | null;
}

/** 一条期产物表态（POST /ai-verdicts 落库后回读的当前值）。 */
export interface ScheduledRunItemVerdict {
  /** 对应步骤产物的 key。 */
  targetRef: string;
  verdict: "agree" | "disagree";
  reason: string | null;
  category: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

/** 上期 accepted 摘要（比对目标与对照展示的依据）。 */
export interface ScheduledRunPreviousRunSummary {
  runId: string;
  scheduledAt: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  savedDocumentIds: string[];
}

/** GET /scheduled-tasks/{taskId}/runs/{runId}/review 响应。 */
export interface ScheduledRunReview {
  run: ScheduledTaskRun;
  stepOutputs: ScheduledRunStepOutput[];
  savedDocuments: ScheduledRunSavedDocument[];
  verdicts: ScheduledRunItemVerdict[];
  previousRun: ScheduledRunPreviousRunSummary | null;
  reviewStatus: ScheduledRunReviewStatus;
  canReview: boolean;
  /** 终态留痕（accepted：采纳备注；returned：退回原因）。 */
  acceptedNote: string | null;
  returnReason: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

/**
 * POST .../save-as-document 响应。`compareWithPreviousParams` 无上期
 * accepted 产物时为 null——「与上一版比对」据此隐藏。
 */
export interface ScheduledRunSaveAsDocumentResult {
  documentId: string;
  startReviewParams: { documentId: string } | null;
  compareWithPreviousParams: { sourceDocumentId: string; targetDocumentId: string } | null;
}

/** GET /scheduled-tasks/summary response payload. */
export interface ScheduledTaskSummary {
  active: number;
  paused: number;
  disabled: number;
  failedLast7Days: number;
}

/**
 * Create payload (ScheduledTaskCreate). `targetType` and `frequency` are
 * structurally required; the target-specific and frequency-specific fields
 * they unlock are validated server-side (422) per contract §4.4 — the picker
 * components enforce the same rules client-side for the live preview.
 */
export interface ScheduledTaskCreatePayload {
  name: string;
  description?: string;
  targetType: ScheduledTaskTargetType;
  instanceId?: string | null;
  /** targetType=pipeline_template 必填（后端 422 校验）。 */
  templateId?: string | null;
  /** pipeline_template 必填；matter_reminder 亦必填。 */
  matterId?: string | null;
  /** pipeline_template 目标的输入解析器。 */
  inputResolver?: ScheduledTaskInputResolver | null;
  /** pipeline_template 目标的解析器配置。 */
  resolverConfig?: ScheduledTaskResolverConfig | null;
  reminderTitle?: string | null;
  reminderNote?: string | null;
  notifyUserIds?: string[];
  /** agent_run 目标的任务目标（周期任务可选，空则每期按模板渲染）；
   * 非 agent_run 目标提交 goal 后端 422。 */
  goal?: string | null;
  /** agent_run 目标的预算三元组（到点建 run 时透传）。 */
  maxSteps?: number | null;
  maxWallClockMinutes?: number | null;
  maxTotalTokens?: number | null;
  frequency: ScheduledTaskFrequency;
  atHour?: number | null;
  atMinute?: number | null;
  byWeekday?: ScheduledTaskWeekday | null;
  byMonthday?: number | null;
  intervalMinutes?: number | null;
  timezone?: string;
  startsAt?: string | null;
  endsAt?: string | null;
  maxRuns?: number | null;
}

/**
 * Update payload (ScheduledTaskUpdate) — the fully-optional subset of Create,
 * minus `targetType` (immutable after creation; changing the target requires
 * creating a new task, per contract §6.2).
 */
export type ScheduledTaskUpdatePayload = Partial<Omit<ScheduledTaskCreatePayload, "targetType">>;

/** Shape accepted by `describeFrequency` — either a full task or an in-progress create-form draft. */
export interface FrequencyPreviewInput {
  frequency: ScheduledTaskFrequency;
  atHour?: number | null;
  atMinute?: number | null;
  byWeekday?: ScheduledTaskWeekday | null;
  byMonthday?: number | null;
  intervalMinutes?: number | null;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Client-side mirror of the backend's `schedule_recurrence.describe()`
 * (contract §5) — used by the create form's live preview before the record
 * exists (and thus before the server can return `frequencyText`). Once a
 * task exists, prefer the server-produced `frequencyText` field; this
 * helper exists to keep the preview in sync with the same wording.
 *
 * Returns "" when the fields required by the current `frequency` are not
 * yet filled in — callers should show a placeholder in that case.
 */
export function describeFrequency(task: FrequencyPreviewInput): string {
  const { frequency, atHour, atMinute, byWeekday, byMonthday, intervalMinutes } = task;

  switch (frequency) {
    case "once":
      return "单次";

    case "hourly":
      if (atMinute == null) return "";
      return `每小时第 ${atMinute} 分钟`;

    case "daily":
      if (atHour == null || atMinute == null) return "";
      return `每天 ${pad2(atHour)}:${pad2(atMinute)}`;

    case "weekly":
      if (atHour == null || atMinute == null || byWeekday == null) return "";
      return `每${WEEKDAY_LABELS[byWeekday]} ${pad2(atHour)}:${pad2(atMinute)}`;

    case "monthly":
      if (atHour == null || atMinute == null || byMonthday == null) return "";
      return `每月 ${byMonthday} 日 ${pad2(atHour)}:${pad2(atMinute)}`;

    case "custom_interval":
      if (intervalMinutes == null) return "";
      return intervalMinutes % 60 === 0
        ? `每 ${intervalMinutes / 60} 小时`
        : `每 ${intervalMinutes} 分钟`;

    default:
      return "";
  }
}
